import { FC } from 'react';
import { Container, createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import Carrousel from './app/components/global/Carrousel';
import Selectivity from './app/components/sections/Selectivity';
import About from './app/components/sections/About';
import Offer from './app/components/sections/Offer';
import Horses from './app/components/sections/Horses';
import Method from './app/components/sections/Method';
import Pricing from './app/components/sections/Pricing';
import Contact from './app/components/sections/Contact';
import Layout from './app/components/routes/Layout';
import Footer from './app/components/footer/Footer';
import Team from './app/components/sections/Team';
import AbeoFeature from './app/components/sections/AbeoFeature';
import { ThemeProvider as CustomThemeProvider, useThemeContext } from './app/components/global/ThemeContext';
import { designTokens } from './app/theme/designTokens';

const AppContent: FC = () => {
  const { darkMode } = useThemeContext();
  const colors = darkMode ? designTokens.colors.dark : designTokens.colors.light;

  const dynamicTheme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        contrastText: darkMode ? '#07142B' : '#FFFFFF',
      },
      background: {
        default: colors.background.default,
        paper: colors.background.elevated,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
      divider: colors.border.default,
    },
    typography: {
      fontFamily: designTokens.typography.fontFamily.sans,
      h1: { fontFamily: designTokens.typography.fontFamily.sans, fontWeight: 500 },
      h2: { fontFamily: designTokens.typography.fontFamily.sans, fontWeight: 500 },
      h3: { fontFamily: designTokens.typography.fontFamily.sans, fontWeight: 500 },
      h4: { fontFamily: designTokens.typography.fontFamily.sans, fontWeight: 500 },
      h5: { fontFamily: designTokens.typography.fontFamily.sans, fontWeight: 500 },
      h6: { fontFamily: designTokens.typography.fontFamily.sans, fontWeight: 500 },
      button: { fontWeight: 700, letterSpacing: '0.01em' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.radius.md,
            textTransform: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${colors.primary}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: `2px solid ${colors.primary}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background.default,
            color: colors.text.primary,
          },
          '::selection': {
            backgroundColor: colors.primary,
            color: darkMode ? '#07142B' : '#FFFFFF',
          },
          'a:focus-visible, button:focus-visible, [role="button"]:focus-visible': {
            outline: `2px solid ${colors.primary}`,
            outlineOffset: '2px',
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      <header style={{ position: 'sticky', top: 0, zIndex: 1100 }}>
        <nav>
          <Layout />
        </nav>
      </header>
      <section id="top">
        <Carrousel />
      </section>
      <section>
        <Selectivity />
      </section>
      <section id="about">
        <About />
      </section>
      <Container maxWidth="lg">
        <section id="abeo">
          <AbeoFeature />
        </section>
      </Container>
      <Container maxWidth="md">
        <section id="offer">
          <Offer />
        </section>
        <section id="horses">
          <Horses />
        </section>
        <section id="method">
          <Method />
        </section>
        <section id="pricing">
          <Pricing />
        </section>
        <section id="team">
          <Team />
        </section>
        <section id="contact">
          <Contact />
        </section>
      </Container>
      <Footer />
    </ThemeProvider>
  );
};

const App: FC = () => (
  <CustomThemeProvider>
    <AppContent />
  </CustomThemeProvider>
);

export default App;
